import { PageLoader } from '@/components/ui/loader';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Quote } from 'lucide-react';
import { Markdown } from '@/lib/markdown';
import { apiGet, apiPost } from '@/lib/api';
import { cn } from '@/lib/utils';
import { FormView, visitorKey } from './public-form';
import type { Block, PageTheme, PublicForm, PublicPage } from './api';

type Item = Record<string, string>;
const text = (block: Block, key: string) => (typeof block[key] === 'string' ? (block[key] as string) : '');
const items = (block: Block) => (Array.isArray(block.items) ? (block.items as Item[]) : []);
const safeHref = (url: string) => (/^(https?:|mailto:|tel:|#|\/)/i.test(url) ? url : '#');

/** A YouTube or Vimeo link as an embeddable address; anything else is not embedded. */
function videoEmbed(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (youtube) return `https://www.youtube-nocookie.com/embed/${youtube[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

/**
 * Draws a landing page from its blocks. The same component is the editor's
 * live preview and the published page, so what is built is what goes out.
 */
export function PageBlocks({
  blocks,
  theme,
  forms,
  pageId,
  preview = false,
}: {
  blocks: Block[];
  theme: PageTheme | null;
  forms: Record<string, PublicForm | null>;
  pageId?: string;
  preview?: boolean;
}) {
  const primary = theme?.primary ?? '#2563eb';
  const dark = theme?.background === 'dark' || theme?.background === 'aurora';
  const muted = dark ? 'text-white/70' : 'text-slate-600';
  const card = dark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-white shadow-sm';
  const button = (label: string, url: string, large = false) =>
    label ? (
      <a
        href={safeHref(url || '#form')}
        className={cn('inline-flex items-center justify-center rounded-xl font-semibold text-white shadow-lg transition hover:brightness-110', large ? 'px-7 py-3.5 text-base' : 'px-5 py-3 text-sm')}
        style={{ backgroundColor: primary, boxShadow: `0 14px 30px -12px ${primary}` }}
      >
        {label}
      </a>
    ) : null;

  return (
    <div
      className={cn('relative min-h-full overflow-hidden', dark ? 'bg-[hsl(230_58%_7%)] text-white' : 'bg-white text-slate-900')}
      style={{ colorScheme: dark ? 'dark' : 'light' }}
    >
      {theme?.background === 'aurora' && (
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[40rem]">
          <div className="absolute -start-32 -top-32 h-[30rem] w-[30rem] rounded-full blur-[130px]" style={{ backgroundColor: `${primary}66` }} />
          <div className="absolute -end-24 top-24 h-[24rem] w-[24rem] rounded-full bg-cyan-400/25 blur-[130px]" />
        </div>
      )}
      <div className="relative">
        {blocks.map((block) => {
          switch (block.type) {
            case 'hero': {
              const left = text(block, 'align') === 'left' && text(block, 'imageUrl');
              return (
                <section key={block.id} className="px-5 py-20 sm:py-28">
                  <div className={cn('mx-auto max-w-6xl', left ? 'grid items-center gap-10 lg:grid-cols-2' : 'text-center')}>
                    <div className={cn(!left && 'mx-auto max-w-3xl')}>
                      {text(block, 'eyebrow') && (
                        <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: primary }}>
                          {text(block, 'eyebrow')}
                        </p>
                      )}
                      <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-6xl">{text(block, 'heading') || 'Your headline'}</h1>
                      {text(block, 'subheading') && <p className={cn('mt-5 text-lg leading-relaxed', muted)}>{text(block, 'subheading')}</p>}
                      <div className={cn('mt-8 flex flex-wrap gap-3', !left && 'justify-center')}>{button(text(block, 'buttonLabel'), text(block, 'buttonUrl'), true)}</div>
                    </div>
                    {text(block, 'imageUrl') && (
                      <img src={text(block, 'imageUrl')} alt="" className={cn('rounded-3xl shadow-2xl', !left && 'mx-auto mt-14 max-h-[34rem]')} />
                    )}
                  </div>
                </section>
              );
            }
            case 'text':
              return (
                <section key={block.id} className="px-5 py-12">
                  <div className="mx-auto max-w-3xl">
                    {text(block, 'heading') && <h2 className="text-3xl font-extrabold tracking-tight">{text(block, 'heading')}</h2>}
                    <Markdown source={text(block, 'body')} className={cn('mt-4 text-[1.05rem]', muted)} />
                  </div>
                </section>
              );
            case 'image':
              return text(block, 'url') ? (
                <section key={block.id} className="px-5 py-10">
                  <figure className="mx-auto max-w-5xl">
                    <img src={text(block, 'url')} alt={text(block, 'alt')} className="w-full rounded-3xl shadow-xl" loading="lazy" />
                    {text(block, 'caption') && <figcaption className={cn('mt-3 text-center text-sm', muted)}>{text(block, 'caption')}</figcaption>}
                  </figure>
                </section>
              ) : null;
            case 'features':
              return (
                <section key={block.id} className="px-5 py-16">
                  <div className="mx-auto max-w-6xl">
                    {text(block, 'heading') && <h2 className="mx-auto max-w-2xl text-center text-3xl font-extrabold tracking-tight sm:text-4xl">{text(block, 'heading')}</h2>}
                    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {items(block).map((item, index) => (
                        <div key={index} className={cn('rounded-2xl border p-6', card)}>
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ backgroundColor: primary }}>
                            <Check className="h-5 w-5" />
                          </span>
                          <h3 className="mt-4 text-lg font-bold">{item.title}</h3>
                          <p className={cn('mt-2 text-sm leading-relaxed', muted)}>{item.body}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              );
            case 'form': {
              const form = forms[text(block, 'formKey')];
              return (
                <section key={block.id} id="form" className="scroll-mt-6 px-5 py-16">
                  <div className="mx-auto grid max-w-5xl items-start gap-10 lg:grid-cols-[1fr_28rem]">
                    <div>
                      <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{text(block, 'heading') || form?.settings.title || 'Get in touch'}</h2>
                      {text(block, 'body') && <Markdown source={text(block, 'body')} className={cn('mt-4', muted)} />}
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 text-slate-900 shadow-2xl">
                      {form ? (
                        preview ? (
                          <div className="pointer-events-none">
                            <FormView form={form} compact />
                          </div>
                        ) : (
                          <FormView form={form} compact landingPageId={pageId} />
                        )
                      ) : (
                        <p className="py-8 text-center text-sm text-slate-500">Choose a form for this block.</p>
                      )}
                    </div>
                  </div>
                </section>
              );
            }
            case 'cta':
              return (
                <section key={block.id} className="px-5 py-14">
                  <div className="mx-auto max-w-5xl rounded-[2rem] px-6 py-14 text-center text-white" style={{ background: `linear-gradient(120deg, ${primary}, #06b6d4)` }}>
                    <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{text(block, 'heading')}</h2>
                    {text(block, 'body') && <p className="mx-auto mt-3 max-w-2xl text-white/85">{text(block, 'body')}</p>}
                    {text(block, 'buttonLabel') && (
                      <a href={safeHref(text(block, 'buttonUrl') || '#form')} className="mt-7 inline-flex rounded-xl bg-white px-6 py-3 font-semibold" style={{ color: primary }}>
                        {text(block, 'buttonLabel')}
                      </a>
                    )}
                  </div>
                </section>
              );
            case 'testimonials':
              return (
                <section key={block.id} className="px-5 py-16">
                  <div className="mx-auto max-w-6xl">
                    {text(block, 'heading') && <h2 className="text-center text-3xl font-extrabold tracking-tight">{text(block, 'heading')}</h2>}
                    <div className="mt-10 grid gap-5 md:grid-cols-3">
                      {items(block).map((item, index) => (
                        <figure key={index} className={cn('rounded-2xl border p-6', card)}>
                          <Quote className="h-6 w-6" style={{ color: primary }} />
                          <blockquote className="mt-3 leading-relaxed">{item.quote}</blockquote>
                          <figcaption className="mt-4 text-sm">
                            <span className="font-semibold">{item.name}</span>
                            {item.role && <span className={muted}> · {item.role}</span>}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                </section>
              );
            case 'faq':
              return (
                <section key={block.id} className="px-5 py-16">
                  <div className="mx-auto max-w-3xl">
                    <h2 className="text-center text-3xl font-extrabold tracking-tight">{text(block, 'heading') || 'Questions'}</h2>
                    <div className="mt-8 space-y-3">
                      {items(block).map((item, index) => (
                        <details key={index} className={cn('group rounded-2xl border px-5 py-4', card)}>
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                            {item.q}
                            <span className="text-xl transition-transform group-open:rotate-45" style={{ color: primary }}>
                              +
                            </span>
                          </summary>
                          <p className={cn('mt-3 whitespace-pre-wrap text-sm leading-relaxed', muted)}>{item.a}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                </section>
              );
            case 'stats':
              return (
                <section key={block.id} className="px-5 py-12">
                  <div className="mx-auto grid max-w-5xl grid-cols-2 gap-6 text-center md:grid-cols-4">
                    {items(block).map((item, index) => (
                      <div key={index}>
                        <p className="text-4xl font-extrabold" style={{ color: primary }}>
                          {item.value}
                        </p>
                        <p className={cn('mt-1 text-sm', muted)}>{item.label}</p>
                      </div>
                    ))}
                  </div>
                </section>
              );
            case 'logos': {
              const images = Array.isArray(block.images) ? (block.images as string[]) : [];
              return (
                <section key={block.id} className="px-5 py-10">
                  {text(block, 'heading') && <p className={cn('text-center text-xs font-semibold uppercase tracking-[0.2em]', muted)}>{text(block, 'heading')}</p>}
                  <div className="mx-auto mt-6 flex max-w-5xl flex-wrap items-center justify-center gap-x-10 gap-y-6">
                    {images.map((src, index) => (
                      <img key={index} src={src} alt="" className="h-8 w-auto opacity-70 grayscale" loading="lazy" />
                    ))}
                  </div>
                </section>
              );
            }
            case 'video': {
              const embed = videoEmbed(text(block, 'url'));
              return embed ? (
                <section key={block.id} className="px-5 py-10">
                  <div className="mx-auto aspect-video max-w-4xl overflow-hidden rounded-3xl shadow-2xl">
                    <iframe src={embed} title="Video" className="h-full w-full" allow="encrypted-media; picture-in-picture" allowFullScreen />
                  </div>
                </section>
              ) : null;
            }
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}

/** dovocrm.com/lp/<slug> — a published landing page. */
export function PublicLandingPage() {
  const { slug = '' } = useParams();
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['landing', slug],
    queryFn: () => apiGet<PublicPage>(`/public/pages/${slug}`),
    retry: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!page) return;
    document.title = page.title;
    const meta = document.querySelector('meta[name="description"]') ?? document.head.appendChild(Object.assign(document.createElement('meta'), { name: 'description' }));
    meta.setAttribute('content', page.description ?? '');
    // The page counts toward the same website analytics as the snippet.
    void apiPost(`/public/site/${page.trackingKey}/collect`, {
      visitorKey: visitorKey(),
      url: window.location.href,
      title: page.title,
      referrer: document.referrer || null,
    }).catch(() => undefined);
  }, [page]);

  if (isLoading) return <PageLoader className="min-h-screen" />;
  if (isError || !page) return <div className="flex min-h-screen items-center justify-center text-slate-600">This page is not available.</div>;

  return (
    <div className="min-h-screen">
      <PageBlocks blocks={page.blocks} theme={page.theme} forms={page.forms} pageId={page.id} />
      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} {page.company}</footer>
    </div>
  );
}
